import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRestaurantCache } from '../../context/RestaurantCacheContext';
import RestaurantCard from '../../components/RestaurantCard';
import { Restaurant } from '../../types';

const RestaurantsPage: React.FC = () => {
  const { 
    publicRestaurants, 
    loadingPublicRestaurants, 
    errorPublicRestaurants,
    loadPublicRestaurants 
  } = useRestaurantCache();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // Filtrar restaurantes basado en el término de búsqueda
  const filteredRestaurants = useMemo(() => {
    if (!searchTerm.trim()) return publicRestaurants;
    
    return publicRestaurants.filter(restaurant => 
      restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      restaurant.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [publicRestaurants, searchTerm]);

  useEffect(() => {
    loadPublicRestaurants();
  }, [loadPublicRestaurants]);

  const handleRestaurantSelect = (restaurantId: string) => {
    console.log('🏪 Restaurante seleccionado:', restaurantId);
    navigate(`/client/restaurant/${restaurantId}/orders`);
  };

  if (loadingPublicRestaurants) {
    return (
      <div className="min-h-screen bg-[#e9ecef] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-[#22B6B2]/20 border-t-[#22B6B2] rounded-full animate-spin mx-auto mb-6"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-[#2C4358] rounded-full animate-spin mx-auto" style={{ animationDelay: '0.5s' }}></div>
          </div>
          <h3 className="text-lg font-bold text-[#2C4358] mb-2">Cargando restaurantes...</h3>
          <p className="text-[#85929e]">Tus restaurantes...</p>
        </div>
      </div>
    );
  }

  if (errorPublicRestaurants) {
    return (
      <div className="min-h-screen bg-[#e9ecef] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">😔</div>
          <h3 className="text-lg font-bold text-[#2C4358] mb-2">Error al cargar restaurantes</h3>
          <p className="text-[#85929e] mb-6">{errorPublicRestaurants}</p>
          <button
            onClick={loadPublicRestaurants}
            className="bg-[#22B6B2] text-white px-6 py-3 rounded-lg hover:bg-[#1a9a96] transition-colors"
          >
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#e9ecef]">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Buscador de restaurantes */}
        <div className="mb-8">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-[#85929e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Buscar restaurantes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-[#dee2e6] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#22B6B2] focus:border-transparent text-[#2C4358] placeholder-[#85929e]"
            />
          </div>
          {searchTerm && (
            <p className="mt-2 text-sm text-[#85929e]">
              {filteredRestaurants.length} restaurante{filteredRestaurants.length !== 1 ? 's' : ''} encontrado{filteredRestaurants.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Lista de restaurantes */}
        {filteredRestaurants.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🍽️</div>
            <h3 className="text-lg font-bold text-[#2C4358] mb-2">
              {searchTerm ? 'No se encontraron restaurantes' : 'No hay restaurantes visitados'}
            </h3>
            <p className="text-[#85929e]">
              {searchTerm 
                ? `No se encontraron restaurantes que coincidan con "${searchTerm}"`
                : 'Por el momento no hay restaurantes visitados, puedes buscar alguno por su nombre.'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRestaurants.map((restaurant: Restaurant) => (
              <RestaurantCard
                key={restaurant.id}
                restaurant={restaurant}
                onSelect={() => handleRestaurantSelect(restaurant.id)}
                showStatus={true}
              />
            ))}
          </div>
        )}

        
      </div>
    </div>
  );
};

export default RestaurantsPage; 